"use client";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";
import { Check, Loader2, X } from "lucide-react";

export const StatefulButton = ({
  children,
  loading = false,
  success = false,
  error = false,
  disabled = false,
  onClick,
  className,
  variant = "default",
  size = "default",
  ...props
}) => {
  const variants = {
    default: "bg-primary hover:bg-primary/90 text-primary-foreground",
    secondary: "bg-secondary hover:bg-secondary/80 text-secondary-foreground",
    outline: "border border-border hover:bg-muted text-foreground",
    ghost: "hover:bg-muted text-foreground",
    danger: "bg-destructive hover:bg-destructive/90 text-destructive-foreground",
    success: "bg-success hover:bg-success/90 text-success-foreground",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    default: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  };

  const isDisabled = disabled || loading;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={cn(
        "relative inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.span
            key="loading"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-2"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading...</span>
          </motion.span>
        ) : success ? (
          <motion.span
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-2 text-success"
          >
            <Check className="h-4 w-4" />
            <span>Done!</span>
          </motion.span>
        ) : error ? (
          <motion.span
            key="error"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-2 text-destructive"
          >
            <X className="h-4 w-4" />
            <span>Error</span>
          </motion.span>
        ) : (
          <motion.span
            key="default"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
          >
            {children}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

// Moving border button (more expensive animation, use sparingly)
export const MovingBorderButton = ({
  children,
  onClick,
  className,
  containerClassName,
  borderRadius = "0.5rem",
  duration = 3000,
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative inline-flex items-center justify-center overflow-hidden rounded-lg p-[1px]",
        containerClassName
      )}
      style={{ borderRadius }}
      {...props}
    >
      {/* Moving gradient border */}
      <span
        className="absolute inset-0 animate-border-beam"
        style={{
          background: `linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.8), transparent)`,
          backgroundSize: "200% 100%",
          animation: `shimmer ${duration}ms linear infinite`,
        }}
      />
      {/* Button content */}
      <span
        className={cn(
          "relative z-10 inline-flex items-center justify-center gap-2 rounded-lg bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted",
          className
        )}
        style={{ borderRadius: `calc(${borderRadius} - 1px)` }}
      >
        {children}
      </span>
    </button>
  );
};
